/** The "detailed" view — a drawer, not a third density.
 *
 *  Rules, rulesets, blocks, theme, language policy and targeting are
 *  variable-height and text-heavy; inlining them at forty-odd nodes would
 *  destroy the overview this page exists to give. A drawer also survives
 *  scrolling, so an operator can pin one notification and compare it against
 *  the rows above and below — which a popover cannot do.
 *
 *  Summary fields come from the timeline payload (already loaded). The full
 *  rule bodies are lazy-loaded from `/notifications/{id}/` only when the drawer
 *  opens, so the overview stays one round trip.
 */
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as notificationsApi from '@/api/notifications';
import { DELIVERY_METHOD_LABELS } from '@/api/notifications';
import type { RuleWire } from '@/api/notificationRulesets';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import {
  eventActions,
  languagePolicyActions,
  notificationFiresActions,
  roleActions,
  rulesetActions,
  themeActions,
} from '../notifications/notificationLinks';
import { DeliveryMethodChips } from './DeliveryMethodChips';
import {
  ORIGIN_HELP,
  ORIGIN_LABELS,
  TOUR_OVERRIDE_GROUPS,
  relativeTime,
} from './format';
import type { TimelineActions, TimelineNotification, TimelineStats } from './model/types';
import { TourLinks } from './TourLinks';
import { WarningBadges } from './WarningBadges';

interface Props {
  notification: TimelineNotification | null;
  onClose: () => void;
  actions?: TimelineActions;
  stats?: TimelineStats;
  /** Ask the timeline to open the test-render dialog for this notification. */
  onOpenTestRender?: (id: number) => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      {typeof children === 'string' ? (
        <Typography variant="body2">{children}</Typography>
      ) : (
        children
      )}
    </Box>
  );
}

export function NodeInspector({
  notification: nf,
  onClose,
  actions,
  stats,
  onOpenTestRender,
}: Props) {
  const open = nf !== null;
  const id = nf?.id ?? null;

  // Only the drawer pays for the detail fetch, and only for saved rows.
  const detail = useQuery({
    queryKey: ['notification-detail', id],
    queryFn: () => notificationsApi.retrieve(id!),
    enabled: open && id !== null,
    staleTime: 30_000,
  });

  const fires = nf?.id != null ? stats?.byNotification[String(nf.id)] : undefined;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; temporary drawers default to
      // zIndex.drawer, so without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 2 } }}
    >
      {nf && (
        <>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {nf.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: 'text.secondary',
                }}
              >
                <ValueActions actions={eventActions(nf.event, { tenantId: nf.tenant?.id ?? null })}>
                  {nf.event}
                </ValueActions>
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close details">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {nf.position && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {nf.position.labelLong}
            </Typography>
          )}

          {nf.warnings.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <WarningBadges
                warnings={nf.warnings}
                cannotFireReason={nf.cannotFireReason}
                canonicalEvent={nf.canonicalEvent}
              />
            </Box>
          )}

          {nf.canFire === false && nf.cannotFireReason === 'aliased' && nf.canonicalEvent && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              Delivery matches notifications against{' '}
              <strong>{nf.canonicalEvent}</strong>, not <strong>{nf.event}</strong>, so this one
              never fires. Re-save it on the canonical code.
            </Alert>
          )}

          {!nf.materialized && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Proposed — nothing has been created yet.
            </Alert>
          )}

          {nf.note && <Field label="Rationale">{nf.note}</Field>}

          <Divider sx={{ my: 1.5 }} />

          <Field label="Origin">
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Chip size="small" label={ORIGIN_LABELS[nf.origin]} variant="outlined" />
              {nf.tenant && (
                <Typography variant="body2">
                  <ValueActions actions={operatorActions(nf.tenant)}>{nf.tenant.name}</ValueActions>
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {ORIGIN_HELP[nf.origin]}
            </Typography>
          </Field>

          {nf.tourOverrideMode !== undefined && (
            <Field label="Scope">
              <Typography variant="body2">
                {TOUR_OVERRIDE_GROUPS.find((g) => g.mode === nf.tourOverrideMode)?.label ??
                  String(nf.tourOverrideMode)}
              </Typography>
            </Field>
          )}

          {nf.tours?.length ? (
            <Field label={`Tours (${nf.tours.length})`}>
              <TourLinks tours={nf.tours} href={actions?.tourHref} variant="list" />
            </Field>
          ) : null}

          {nf.audience && (
            <Field label="Audience">
              {nf.audience.guests || nf.audience.observers || nf.audience.roles.length ? (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {nf.audience.guests && <Typography variant="body2">Guests</Typography>}
                  {nf.audience.observers && <Typography variant="body2">Observers</Typography>}
                  {nf.audience.roles.map((r) => (
                    <Typography key={r.id} variant="body2">
                      <ValueActions actions={roleActions(r, { tenantId: nf.tenant?.id ?? null })}>
                        {r.name}
                      </ValueActions>
                    </Typography>
                  ))}
                </Stack>
              ) : (
                'Nobody selected'
              )}
            </Field>
          )}

          <Field label="Channels">
            <DeliveryMethodChips methods={nf.deliveryMethods} />
          </Field>

          {nf.ruleSummary && (
            <Field label="Targeting rules">
              <Typography variant="body2">
                {nf.ruleSummary.count === 0
                  ? 'No rules — matches every event'
                  : `${nf.ruleSummary.count} rule${nf.ruleSummary.count === 1 ? '' : 's'} (${
                      nf.ruleSummary.matchType === 1 ? 'match any' : 'match all'
                    })`}
              </Typography>
              {nf.ruleSummary.rulesets.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {nf.ruleSummary.rulesets.map((rs) => {
                    const chip = (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={rs.codename ?? rs.name}
                        sx={{ height: 20, fontSize: '0.6875rem' }}
                      />
                    );
                    return rs.id === null ? (
                      <Box component="span" key={rs.name}>
                        {chip}
                      </Box>
                    ) : (
                      <ValueActions
                        chip
                        key={rs.id}
                        actions={rulesetActions({ id: rs.id, codename: rs.codename })}
                      >
                        {chip}
                      </ValueActions>
                    );
                  })}
                </Stack>
              )}
            </Field>
          )}

          <Field label="Composition">
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                size="small"
                variant="outlined"
                label={`${nf.blockRefCount ?? 0} block${nf.blockRefCount === 1 ? '' : 's'}`}
                sx={{ height: 20, fontSize: '0.6875rem' }}
              />
              {nf.disableDefaultBlocks && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="default blocks off"
                  sx={{ height: 20, fontSize: '0.6875rem' }}
                />
              )}
              {nf.languagePolicy &&
                (() => {
                  const chip = (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`policy: ${nf.languagePolicy!.codename}`}
                      sx={{ height: 20, fontSize: '0.6875rem' }}
                    />
                  );
                  const pid = nf.languagePolicy.id;
                  return pid === null ? (
                    chip
                  ) : (
                    <ValueActions
                      chip
                      actions={languagePolicyActions({ id: pid, codename: nf.languagePolicy.codename })}
                    >
                      {chip}
                    </ValueActions>
                  );
                })()}
            </Stack>
          </Field>

          {fires && (
            <Field label="Last 30 days">
              {(() => {
                const text =
                  fires.fires === 0
                    ? 'No sends'
                    : `${fires.fires} sends${fires.errors ? `, ${fires.errors} failed` : ''}${
                        fires.lastTs ? ` · last ${relativeTime(fires.lastTs)}` : ''
                      }`;
                return id === null ? (
                  <Typography variant="body2">{text}</Typography>
                ) : (
                  <Typography variant="body2">
                    <ValueActions actions={notificationFiresActions(id)}>{text}</ValueActions>
                  </Typography>
                );
              })()}
            </Field>
          )}

          {id !== null && (
            <>
              <Divider sx={{ my: 1.5 }} />
              {detail.isPending && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Loading full configuration…
                  </Typography>
                </Stack>
              )}
              {detail.isError && (
                <Alert severity="warning">
                  Couldn’t load the full configuration. The summary above is still accurate.
                </Alert>
              )}
              {detail.data && <DetailSections detail={detail.data} />}
            </>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {actions?.notificationHref && id !== null && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                href={actions.notificationHref(id)}
                target="_blank"
                rel="noopener"
              >
                Edit
              </Button>
            )}
            {actions?.deliveryLogsHref && id !== null && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                href={actions.deliveryLogsHref(id)}
                target="_blank"
                rel="noopener"
              >
                Delivery logs
              </Button>
            )}
            {actions?.anatomyHref && id !== null && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                href={actions.anatomyHref(id)}
                target="_blank"
                rel="noopener"
              >
                Anatomy
              </Button>
            )}
            {id !== null && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenTestRender?.(id)}
              >
                Test render
              </Button>
            )}
          </Stack>
        </>
      )}

    </Drawer>
  );
}

/** The parts only the full detail payload carries. */
function DetailSections({ detail }: { detail: notificationsApi.NotificationDetail }) {
  const rules = detail.rules ?? [];
  const methods = detail.delivery_methods ?? [];
  return (
    <>
      {rules.length > 0 && (
        <Field label={`Rules (${rules.length})`}>
          <Stack spacing={0.25}>
            {rules.map((rule) => (
              <Typography
                key={rule.id}
                variant="caption"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: 'text.secondary',
                  wordBreak: 'break-word',
                }}
              >
                {describeRule(rule)}
              </Typography>
            ))}
          </Stack>
        </Field>
      )}
      {methods.length > 0 && (
        <Field label="Per-channel rendering">
          <Stack spacing={0.25}>
            {methods.map((dm) => (
              <Typography key={dm.id} variant="caption" color="text.secondary">
                {DELIVERY_METHOD_LABELS[dm.method] ?? `method ${dm.method}`}
                {dm.theme !== null && (
                  <>
                    {' · '}
                    <ValueActions
                      actions={themeActions({
                        id: dm.theme,
                        codename: detail.refs.themes[String(dm.theme)]?.codename ?? null,
                      })}
                    >
                      theme #{dm.theme}
                    </ValueActions>
                  </>
                )}
                {dm.enabled ? '' : ' · off'}
              </Typography>
            ))}
          </Stack>
        </Field>
      )}
    </>
  );
}

/** A one-line, human-ish rendering of a rule row. The editor owns the real
 *  presentation; this only has to be recognisable at a glance. */
function describeRule(rule: RuleWire): string {
  if (rule.nested_ruleset !== null) return `ruleset #${rule.nested_ruleset}`;
  const target = rule.tpl !== null ? `tpl ${rule.tpl}` : (rule.field ?? 'rule');
  const detail = rule.args ? JSON.stringify(rule.args) : (rule.value ?? '');
  return `${rule.negate ? 'NOT ' : ''}${target} ${detail}`.trim();
}
